import React, { useState, useEffect } from 'react';
import { FiEye, FiCheck, FiX } from 'react-icons/fi';
import Swal from 'sweetalert2';
import Layout from '../components/Layout';
import PageHeader from '../components/PageHeader';
import DataTable from '../components/DataTable';
import Pagination from '../components/Pagination';
import { apiClient } from '../utils/api';

const ContactUs = ({ user, onLogout }) => {
  const [contactMessages, setContactMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit] = useState(10);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);

  const fetchContactMessages = async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.getContactUs(page, limit);
      
      if (response.data && response.data.data) {
        setContactMessages(response.data.data);
        setTotal(response.data.total);
        setTotalPages(Math.ceil(response.data.total / limit));
        setCurrentPage(response.data.page);
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch contact messages');
      console.error('Error fetching contact messages:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchContactMessageDetails = async (id) => {
    setModalLoading(true);
    try {
      const response = await apiClient.getContactUsById(id);
      if (response.data) {
        setSelectedMessage(response.data);
      }
    } catch (err) {
      console.error('Error fetching contact message details:', err);
      Swal.fire('Error!', err.message, 'error');
    } finally {
      setModalLoading(false);
    }
  };

  const handleApprove = async (id) => {
    const result = await Swal.fire({
      title: 'Approve Message',
      text: 'Are you sure you want to approve this contact message?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#10b981',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, approve it!',
      cancelButtonText: 'Cancel',
      customClass: {
        popup: 'rounded-lg shadow-xl',
        confirmButton: 'bg-[#10b981] text-white font-bold py-2 px-4 rounded',
        cancelButton: 'bg-[#6b7280] text-white font-bold py-2 px-4 rounded',
      }
    });

    if (result.isConfirmed) {
      try {
        await apiClient.approveContactUs(id);
        Swal.fire({
          title: 'Success!',
          text: 'Contact message approved successfully.',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false,
        });
        fetchContactMessages(currentPage);
      } catch (err) {
        Swal.fire('Error!', err.message, 'error');
      }
    }
  };

  const handleReject = async (id) => {
    const result = await Swal.fire({
      title: 'Reject Message',
      text: 'Are you sure you want to reject this contact message?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, reject it!',
      cancelButtonText: 'Cancel',
      customClass: {
        popup: 'rounded-lg shadow-xl',
        confirmButton: 'bg-[#ef4444] text-white font-bold py-2 px-4 rounded',
        cancelButton: 'bg-[#6b7280] text-white font-bold py-2 px-4 rounded',
      }
    });

    if (result.isConfirmed) {
      try {
        await apiClient.rejectContactUs(id);
        Swal.fire({
          title: 'Success!',
          text: 'Contact message rejected successfully.',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false,
        });
        fetchContactMessages(currentPage);
      } catch (err) {
        Swal.fire('Error!', err.message, 'error');
      }
    }
  };

  const handleViewMessage = async (id) => {
    await fetchContactMessageDetails(id);
    setShowModal(true);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchContactMessages(page);
  };

  const handlePageSizeChange = (newPageSize) => {
    // Handle page size change if needed
  };

  const handleRowAction = (actionKey, row, index) => {
    // Handle row actions if needed
  };

  const handleRowSelect = (selectedRows) => {
    // Handle row selection if needed
  };

  const handleColumnFilter = (key, value) => {
    // Handle column filtering if needed
  };

  const handleSort = (sortConfig) => {
    // Handle sorting if needed
  };

  useEffect(() => {
    fetchContactMessages();
  }, []);

  const getStatusBadge = (status) => {
    const statusColors = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[status] || 'bg-gray-100 text-gray-800'}`}>
        {status}
      </span>
    );
  };

  const columns = [
    {
      key: 'id',
      title: 'ID',
      render: (value) => (
        <div className="text-sm font-medium text-gray-900">
          #{value}
        </div>
      )
    },
    {
      key: 'name',
      title: 'Name',
      render: (value) => (
        <div className="text-sm text-gray-900">
          {value}
        </div>
      )
    },
    {
      key: 'email',
      title: 'Email',
      render: (value) => (
        <div className="text-sm text-gray-900">
          {value}
        </div>
      )
    },
    {
      key: 'message',
      title: 'Message',
      render: (value) => (
        <div className="text-sm text-gray-900 max-w-xs truncate">
          {value}
        </div>
      ),
      sortable: false
    },
    {
      key: 'status',
      title: 'Status',
      render: (value) => getStatusBadge(value)
    },
    {
      key: 'actions',
      title: 'Actions',
      render: (value, row) => (
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handleViewMessage(row.id)}
            className="text-blue-600 hover:text-blue-800 p-1"
            title="View Details"
          >
            <FiEye className="h-4 w-4" />
          </button>
          {row.status === 'pending' && (
            <>
              <button
                onClick={() => handleApprove(row.id)}
                className="text-green-600 hover:text-green-800 p-1"
                title="Approve"
              >
                <FiCheck className="h-4 w-4" />
              </button>
              <button
                onClick={() => handleReject(row.id)}
                className="text-red-600 hover:text-red-800 p-1"
                title="Reject"
              >
                <FiX className="h-4 w-4" />
              </button>
            </>
          )}
        </div>
      ),
      sortable: false
    }
  ];

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        <PageHeader
          title="Contact Us"
          subtitle="Manage contact us messages"
          user={user}
        />

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="text-red-800 text-sm">
              {error}
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow">
          {/* Header */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                {loading && (
                  <div className="flex items-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-500"></div>
                    <span className="text-sm text-gray-600">Loading...</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Data Table */}
          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                <span className="ml-2 text-gray-600">Loading data...</span>
              </div>
            ) : (
              <DataTable
                data={contactMessages}
                columns={columns}
                loading={loading}
                searchable={false}
                filterable={false}
                exportable={true}
                actions={[]}
                onRowAction={handleRowAction}
                emptyMessage="No contact messages available"
              />
            )}
          </div>

          {/* Pagination */}
          {!loading && contactMessages.length > 0 && (
            <div className="p-6 border-t border-gray-200">
              <Pagination
                current={currentPage}
                total={total}
                pageSize={limit}
                onChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                showSizeChanger={true}
                showTotal={true}
              />
            </div>
          )}
        </div>
      </div>

      {/* Contact Message Details Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Contact Message Details</h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FiX className="h-5 w-5" />
              </button>
            </div>

            {modalLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500"></div>
                <span className="ml-2 text-gray-600">Loading details...</span>
              </div>
            ) : selectedMessage ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">ID</label>
                    <p className="mt-1 text-sm text-gray-900">#{selectedMessage.id}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Status</label>
                    <div className="mt-1">
                      {getStatusBadge(selectedMessage.status)}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Name</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedMessage.name}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedMessage.email}</p>
                </div>

                {selectedMessage.subject && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Subject</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedMessage.subject}</p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700">Message</label>
                  <div className="mt-1 p-3 bg-gray-50 rounded-md">
                    <p className="text-sm text-gray-900 whitespace-pre-wrap">{selectedMessage.message}</p>
                  </div>
                </div>

                {selectedMessage.status === 'pending' && (
                  <div className="flex justify-end space-x-3 pt-4 border-t">
                    <button
                      onClick={() => {
                        setShowModal(false);
                        handleApprove(selectedMessage.id);
                      }}
                      className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                    >
                      <FiCheck className="h-4 w-4 inline mr-2" />
                      Approve
                    </button>
                    <button
                      onClick={() => {
                        setShowModal(false);
                        handleReject(selectedMessage.id);
                      }}
                      className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                    >
                      <FiX className="h-4 w-4 inline mr-2" />
                      Reject
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                No details available
              </div>
            )}
          </div>
        </div>
      )}
    </Layout>
  );
};

export default ContactUs;
